/**
 * MermaidDiagram — client-side render of a canon Mermaid source string (wave
 * glass-1, #790). The SPA takes the pinned renderer dependency (mermaid,
 * exact version in package.json — the #790 body's ruled mechanic); the
 * source string is canon carried verbatim by the build step, so what renders
 * is the diagram the canon file holds, not a redrawing.
 *
 * Render is async (mermaid.render returns SVG markup); a failed render shows
 * the error text and the verbatim source rather than a blank pane — a broken
 * render must be visible, never silent. It is visible HERE, in the diagram's
 * own card, and nowhere else (#956): Mermaid's own "Syntax error in text"
 * graphic is suppressed, because Mermaid draws it into a temporary element on
 * document.body and leaves it there, below the app, where it names no diagram
 * and no cause. Every failure is also logged to the console, including one
 * whose card unmounted before the render settled.
 *
 * A tab that loaded before a deploy still holds the old build, whose lazy
 * Mermaid chunks (the flowchart code) carry the old hashed names; the worker
 * answers an unknown asset path with index.html, so the import fails in the
 * browser (#956 F14). The card names that failure by the browser's own
 * message and says what a reload does.
 */

import { useEffect, useId, useState } from "react";
import mermaid from "mermaid";

mermaid.initialize({
  startOnLoad: false,
  theme: "neutral",
  // securityLevel "strict" strips the canon's <br/>/<b>/<i> node labels;
  // "antiscript" keeps that HTML while still stripping script. The source is
  // build-bundled canon, not user input.
  securityLevel: "antiscript",
  // A failed render removes Mermaid's temporary element and rethrows, rather
  // than drawing the error graphic into it on document.body and leaving it
  // there (#956). The card below shows the failure instead.
  suppressErrorRendering: true,
});

/** The browser's message when a lazily imported chunk could not be fetched —
 *  Chrome, Firefox and Safari word it differently; these are the three
 *  prefixes TanStack Router's isModuleNotFoundError matches. */
const CHUNK_LOAD_FAILURE =
  /^(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/;

export function MermaidDiagram({ source }: { source: string }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    mermaid
      .render(`glass-${id}`, source)
      .then((r) => {
        if (!cancelled) setSvg(r.svg);
      })
      .catch((e: unknown) => {
        // Logged before the cancelled check: a failure whose card has
        // unmounted is still a failure (#956).
        console.error(`Mermaid render failed (glass-${id}):`, e);
        // Mermaid builds each diagram in a temporary div#d<id> on
        // document.body; suppressErrorRendering removes it only on the two
        // error paths it guards, not on every throw after the div exists.
        document.getElementById(`dglass-${id}`)?.remove();
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [id, source]);

  if (error !== null) {
    return (
      <div data-testid="mermaid-error">
        <p className="mb-2 text-sm text-error">
          Diagram failed to render: {error}
        </p>
        {CHUNK_LOAD_FAILURE.test(error) ? (
          <p className="mb-2 text-sm" data-testid="mermaid-reload-hint">
            This tab could not fetch the diagram's code. Reloading the page
            fetches it again.
          </p>
        ) : null}
        <pre className="overflow-x-auto rounded-md border border-border bg-muted p-3 text-xs">
          {source}
        </pre>
      </div>
    );
  }
  if (svg === null) {
    return (
      <div className="text-sm text-plane-muted" data-testid="mermaid-loading">
        Rendering…
      </div>
    );
  }
  return (
    <div
      className="overflow-x-auto [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full"
      data-testid="mermaid-diagram"
      // Mermaid's own sanitized SVG output (securityLevel antiscript).
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
